/**
 * OwlMascot – the canonical Owl illustration.
 *
 * Single component, three size variants, one image source. Centralised
 * so future placement changes (or swapping the source PNG to something
 * else later) happen in one place. The image lives at
 * /owl-mascot.png – Vite serves files from owl/public/ at the root URL
 * without any build pipeline transformation, so the cache headers come
 * from nginx and the asset survives SPA hash changes.
 *
 * Sizes (intentional, not freeform):
 *   - "nav"     →  28px  rounded square. Pairs with the "Owl · Beta" text.
 *   - "card"    →  56px  for the WelcomeCard left rail.
 *   - "chat"    →  40px  for the chat-drawer empty state.
 *   - "hero"    →  96px  for big empty/error states (404, "Owl couldn't reach data").
 *
 * Decorative by default (`aria-hidden`). Pass `label` when the mascot
 * IS the only visual cue for what something is (rare – we usually
 * pair it with text).
 */
export function OwlMascot({
  size = "nav",
  className = "",
  label,
}: {
  size?: "nav" | "card" | "chat" | "hero";
  className?: string;
  label?: string;
}) {
  const px =
    size === "nav"  ? 28 :
    size === "card" ? 56 :
    size === "chat" ? 40 :
    /* hero */        96;
  const alt = label ?? "";
  return (
    <img
      src="/owl-mascot.png"
      alt={alt}
      aria-hidden={!label}
      width={px}
      height={px}
      // object-contain so non-square sources don't distort. The PNG is
      // 311x399 portrait so a square viewport without `object-contain`
      // would stretch the owl horizontally.
      className={`inline-block shrink-0 object-contain ${className}`}
      style={{ width: px, height: px }}
      loading="eager"   // small file, above-the-fold uses dominate
      decoding="async"
    />
  );
}
